import { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const ICONS = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const ICON_COLORS = {
  success: 'text-green-500 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
  warning: 'text-amber-500 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  error: 'text-red-500 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
  info: 'text-blue-500 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
};

const STORAGE_KEY = 'arasync_notifications';
const MAX_NOTIFICATIONS = 50;

const getNotifications = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveNotifications = (notifs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifs.slice(0, MAX_NOTIFICATIONS)));
};

export const addNotification = (notification) => {
  const notifs = getNotifications();
  notifs.unshift({
    id: Date.now().toString(),
    timestamp: new Date().toISOString(),
    read: false,
    ...notification,
  });
  saveNotifications(notifs);
};

const NotificationCenter = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const panelRef = useRef(null);

  useEffect(() => {
    if (isOpen) setNotifications(getNotifications());
  }, [isOpen]);

  const markAllRead = () => {
    const updated = notifications.map((n) => ({ ...n, read: true }));
    setNotifications(updated);
    saveNotifications(updated);
  };

  const clearAll = () => {
    setNotifications([]);
    saveNotifications([]);
  };

  const removeOne = (id) => {
    const updated = notifications.filter((n) => n.id !== id);
    setNotifications(updated);
    saveNotifications(updated);
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-[9998]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notification center"
        className="fixed right-0 top-0 bottom-0 w-96 max-w-full bg-white dark:bg-gray-800 shadow-2xl z-[9999] flex flex-col animate-modal-content"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h2>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Mark all read
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                >
                  Clear
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close notifications"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <BellIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const IconComponent = ICONS[notif.type] || ICONS.info;
              const iconColor = ICON_COLORS[notif.type] || ICON_COLORS.info;
              return (
                <div
                  key={notif.id}
                  className={`flex gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    !notif.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${iconColor}`}
                  >
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white">{notif.title}</p>
                    {notif.message && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {notif.message}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {formatTime(notif.timestamp)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOne(notif.id)}
                    aria-label="Dismiss"
                    className="p-0.5 text-gray-300 hover:text-gray-500 dark:hover:text-gray-400 shrink-0"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;
